import React, { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Card, Button, Input, Field, Alert, Badge, Textarea, Select } from './ui';

/**
 * The official wording of the standard forms, pasted once by admin (migration
 * 0065). The database fixes their structure and scoring; what is text is the
 * title, the instruction the patient reads, the questions, and the answer
 * options every question shares.
 *
 * Nothing here types that wording: it comes from the publisher, and a slip in
 * a validated instrument changes what it measures.
 */

const StandardFormText = ({ onSaved }) => {
  const [forms, setForms] = useState([]);
  const [code, setCode] = useState('');
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase.rpc('my_forms');
    if (loadError) { setError(loadError.message); return; }
    const standard = (data ?? []).filter((form) => form.code);
    setForms(standard);
    setCode((current) => current || standard[0]?.code || '');
  }, []);

  useEffect(() => { load(); }, [load]);

  const form = forms.find((row) => row.code === code);

  useEffect(() => {
    if (!form) { setDraft(null); return; }
    setDraft({
      title: form.title,
      description: form.description ?? '',
      prompts: form.questions.map((question) => question.prompt ?? ''),
      labels: [...(form.questions[0]?.labels ?? [])],
    });
  }, [form]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error: saveError } = await supabase.rpc('set_standard_form_text', {
      p_code: code,
      p_title: draft.title,
      p_description: draft.description,
      p_prompts: draft.prompts,
      p_labels: draft.labels,
    });
    setBusy(false);
    if (saveError) { setError(saveError.message); return; }
    setNotice(t('forms.textSaved', { title: draft.title }));
    load();
    // Formularios shows whether each standard form is ready to send.
    onSaved?.();
  };

  if (!forms.length) return null;

  return (
    <Card title={t('forms.standardTextTitle')} subtitle={t('forms.standardTextHint')}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}

        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('forms.pickForm')}>
            <Select className="w-48" value={code} onChange={(event) => { setCode(event.target.value); setNotice(null); }}>
              {forms.map((row) => <option key={row.code} value={row.code}>{row.title}</option>)}
            </Select>
          </Field>
          {form && (
            <Badge tone={form.ready ? 'success' : 'warning'}>
              {t(form.ready ? 'forms.standardReady' : 'forms.standardMissing')}
            </Badge>
          )}
        </div>

        {draft && (
          <>
            <Field label={t('forms.formTitle')}>
              <Input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
            </Field>
            <Field label={t('forms.standardDescription')}>
              <Textarea rows={2} value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            </Field>

            <div className="space-y-2">
              {draft.prompts.map((prompt, index) => (
                <Field key={`${code}-prompt-${index + 1}`} label={t('forms.standardPrompt', { n: index + 1 })}>
                  <Textarea
                    rows={2}
                    value={prompt}
                    onChange={(event) => {
                      const prompts = [...draft.prompts];
                      prompts[index] = event.target.value;
                      setDraft({ ...draft, prompts });
                    }}
                  />
                </Field>
              ))}
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">{t('forms.standardLabels')}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {draft.labels.map((label, value) => (
                  <Field key={`${code}-label-${value}`} label={t('forms.standardLabelFor', { value })}>
                    <Input
                      value={label}
                      onChange={(event) => {
                        const labels = [...draft.labels];
                        labels[value] = event.target.value;
                        setDraft({ ...draft, labels });
                      }}
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} loading={busy}><Save size={16} />{t('forms.saveText')}</Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default StandardFormText;
